/* eslint-disable no-empty */
import API from './api';
import NotFoundException from './exceptions/not-found.exception';
import OrderExpiredException from './exceptions/order-expired.exception';
import InternalServerErrorException from './exceptions/internal-server-error.exception';
import PaymentNotAuthorizedException from './exceptions/payment-not-authorized.exception';
import ProductOutOfStockException from './exceptions/product-out-of-stock.exception';
import OrderPaidException from './exceptions/order-paid.exception';
import LocalStorageService from '../../services/local-storage.service';
import { Records } from '../interfaces/commons.interface';
import { Order, OrderLGPD } from './interfaces/order.interface';
import BoletoExpiredException from './exceptions/boleto-expired.exception';
import { ExpirationTime } from './interfaces/expiration-time.interface';
import PixExpiredException from './exceptions/pix-expired.exception';
import PixRefusedException from './exceptions/pix-refused.exception';
import SessionIdNotAcceptableException from './exceptions/session-id-not-acceptable-exception';
import { LegalNatureType } from '../../constants/legalNature.enum';
import SaleToCnpjNotAllowed from './exceptions/sale-to-cnpj-not-allowed.exception';
import { AuthorizationTerm } from './interfaces/authorizarionTerm';
import OrderCannotBeCanceled from './exceptions/order-cannot-be-canceled-exception';
import SellerDoesNotSellToCnpj from './exceptions/seller-does-not-sell-to-cnpj.exception';
import { SessionInterface } from './interfaces/session.interface';

class CaipirinhaRequest {
  public static async authCustomer(id: string, document: string) {
    const response = await API.get<Records<{ token: string }>>(
      `/v1/auth/customer/${id}/${document}`,
    );
    if (response.ok && response.data) {
      return response.data.records[0].token;
    }
    return null;
  }

  public static async getOrderPaidByIdAndDocument(
    id: string,
    document: string,
    legalNature: LegalNatureType,
  ) {
    LocalStorageService.setItem('order', { id, document, legalNature });

    const response = await API.get<Records<OrderLGPD>>(
      `/v1/orders/${id}/${document}/paid`,
    );

    if (response.ok && response.data) {
      return response.data.records[0];
    }

    if (response.status === 404) {
      throw new NotFoundException();
    }

    if (response.status === 401) {
      console.log('Erro 401');

      throw new NotFoundException();
    }

    if (response.status === 410) {
      throw new OrderExpiredException();
    }

    throw new InternalServerErrorException();
  }

  public static async getOrderByIdAndDocument(
    id: string,
    document: string,
    legalNature: LegalNatureType,
  ) {
    LocalStorageService.setItem('order', { id, document, legalNature });
    const response = await API.get<Records<OrderLGPD>>(
      `/v1/orders/${id}/${document}`,
    );

    if (response.ok && response.status === 201 && response.data) {
      return response.data.records[0];
    }

    if (response.status === 200) {
      throw new OrderPaidException();
    }

    if (response.status === 404) {
      throw new NotFoundException();
    }

    if (response.status === 401) {
      throw new NotFoundException();
    }

    if (response.status === 406) {
      if (response.data && response.data.errorCode === 4009) {
        throw new ProductOutOfStockException();
      }

      throw new PaymentNotAuthorizedException();
    }

    if (response.status === 410) {
      if (response.data && response.data.errorCode === 20025) {
        throw new BoletoExpiredException();
      }

      if (response.data && response.data.errorCode === 20026) {
        throw new PixExpiredException();
      }

      throw new OrderExpiredException();
    }

    if (response.status === 412) {
      if (response.data && response.data.errorCode === 4043) {
        throw new SaleToCnpjNotAllowed();
      }
    }

    throw new InternalServerErrorException();
  }

  public static async getNameAndExpirationTimeById(id: string) {
    try {
      LocalStorageService.clear();
    } catch {}

    const response = await API.get<Records<ExpirationTime>>(
      `/v1/orders/${id}/name`,
    );

    if (response.ok && response.data) {
      return response.data.records[0];
    }

    return null;
  }

  public static async confirmPayment(
    id: string,
    encrypted?: string,
    fingerprint?: string,
    sessionId?: string,
  ) {
    const response = await API.put<Records<Order>>(
      `/v1/orders/${id}/checkout`,
      {
        encrypted,
        fingerprint,
        session: {
          id: sessionId,
        },
      },
    );

    if (response.ok && response.data) {
      return response.data.records[0];
    }

    if (response.status === 406) {
      if (response.data && response.data.errorCode === 20410) {
        throw new PixRefusedException();
      }

      if (response.data && response.data.errorCode === 4009) {
        throw new ProductOutOfStockException();
      }

      if (response.data && response.data.errorCode === 4024) {
        throw new SessionIdNotAcceptableException();
      }

      throw new PaymentNotAuthorizedException();
    }

    if (response.status === 410) {
      throw new ProductOutOfStockException(
        response.data && JSON.stringify(response.data.records),
      );
    }

    if (response.status === 412) {
      if (response.data && response.data.errorCode === 4045) {
        throw new SellerDoesNotSellToCnpj();
      }
    }

    throw new InternalServerErrorException();
  }

  public static async deleteOrder(id: string) {
    const response = await API.delete<Records<unknown>>(`/v1/orders/${id}`);

    if (response.ok) {
      return true;
    }

    if (response.status === 412 && response.data?.errorCode === 4044) {
      throw new OrderCannotBeCanceled();
    }

    throw new InternalServerErrorException();
  }

  public static async getBoleto(order: Order) {
    const response = await API.get<string>(`/v1/orders/${order.id}/boleto`);
    if (response.ok && response.data) {
      return response.data;
    }
    return null;
  }

  public static async getAuthorizationTerm(id: string, serviceId: string) {
    const response = await API.get<Records<AuthorizationTerm>>(`/v1/orders/${id}/services/${serviceId}/tac`);
    if (response.ok && response.data) {
      return response.data.records[0];
    }
    throw new InternalServerErrorException();
  }

  public static async getSessionId(id: string, document: string) {
    const response = await API.get<SessionInterface>(
      `/v1/orders/${id}/${document}/session`,
    );

    if (response.ok && response.data) {
      return response.data;
    }

    throw new SessionIdNotAcceptableException();
  }
}

export default CaipirinhaRequest;
